import { queryOptions, type QueryClient } from "@tanstack/react-query";
import type { IssueMetadataService } from "@/services/issue-metadata.service";
import type { TJobRoleRecommendation } from "@/types/job-role";
import { invalidateScoped } from "./invalidation";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

const DOMAIN = "role-assignment-candidates";

/** What a job role's work is ranked over, as its recommendation reports it. */
export type RoleAssignmentWindow = Pick<
  TJobRoleRecommendation,
  "window_start" | "window_end" | "total_estimate_minutes"
>;

/**
 * Every assignable person on a project ranked over one job role's window and
 * total estimate, for the Smart Assignment panel's pick-someone-else list
 * (NG-3289): the task Assignee field's pool and ranking, over the role's work.
 * Qualifications rank first; without a full window, ties are alphabetical
 * with no hours. Access-sensitive (contract §2): it is gated by `project.edit`
 * and `capacity.read`,
 * so it is reused for 60 s and re-read on the next open after that.
 *
 * Key: `[slug, uid, 'role-assignment-candidates', projectId, start, end, estimate, skillIds]`.
 *
 * @writers Smart Assign, through {@link roleAssignmentCandidatesChanged}: an
 * assignment moves the assignee's booked time, so the next open re-reads at
 * once. Any other capacity write shows after the 60 s window.
 */
export function roleAssignmentCandidatesQuery(
  service: Pick<IssueMetadataService, "listAllDraftAssignmentCandidates">,
  scope: QueryScope,
  projectId: string,
  {
    window_start: start,
    window_end: end,
    total_estimate_minutes: estimateMinutes,
  }: RoleAssignmentWindow,
  skillIds: readonly string[] = [],
) {
  const requiredSkills = [...new Set(skillIds)].sort();
  // Hours need both ends of the window; the estimate travels in hours, as the
  // create dialog sends it, and the endpoint rounds it back to minutes.
  const ranked =
    start && end
      ? { startDate: start, endDate: end, estimateHours: String(estimateMinutes / 60) }
      : {};
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: scopedKey(
      scope,
      DOMAIN,
      projectId,
      start ?? "",
      end ?? "",
      estimateMinutes,
      requiredSkills,
    ),
    queryFn: scopedRead(
      (request) =>
        service.listAllDraftAssignmentCandidates(
          scope.tenant,
          projectId,
          { ...ranked, ...(requiredSkills.length ? { skillIds: requiredSkills } : {}) },
          request,
        ),
      { retry: "safe-read" },
    ),
  });
}

/**
 * Mark every role-candidates entry of `scope` stale after a Smart Assign, so
 * the list's next open shows the assignee's new hours. Never rejects; a failed
 * re-read is the cache's to log.
 */
export function roleAssignmentCandidatesChanged(client: QueryClient, scope: QueryScope): void {
  invalidateScoped(client, scope, DOMAIN).catch((error: unknown) => {
    console.warn("[role-assignment-candidates] re-reading after a Smart Assign failed", {
      tenant: scope.tenant,
      error,
    });
  });
}
